import { useState } from 'react';
import type { ReactElement, ReactNode } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  ABSTIMMUNGSSTATUS_BESCHRIFTUNG,
  ABSTIMMUNG_GRENZEN,
  API_ERROR_CODES,
  EXPORTFORMAT_BESCHRIFTUNG,
  beschreibeAuswahlregel,
  istAktiv,
} from '@mcikm-vote/shared';
import type {
  Abstimmung,
  Beteiligung,
  Ergebnis,
  ExportEintrag,
  ExportformatWert,
} from '@mcikm-vote/shared';

import {
  useAbstimmung,
  useAbstimmungAktion,
  useAbstimmungDuplizieren,
  useAbstimmungLoeschen,
  useBerichtigung,
  useBeteiligung,
  useErgebnis,
  useFeststellung,
} from '../../admin/abstimmungenApi';
import {
  useAbstimmungExportieren,
  useAbstimmungExportstand,
  useExportLoeschen,
} from '../../admin/exporteApi';
import { useVeranstaltung, useVeranstaltungAktion } from '../../admin/veranstaltungenApi';
import { BestaetigungsDialog } from '../../components/BestaetigungsDialog';
import { Ergebnisbalken } from '../../components/Ergebnisbalken';
import { ExportFormatZeile } from '../../components/ExportFormatZeile';
import { Fehlermeldung } from '../../components/Fehlermeldung';
import { FormatierterText } from '../../components/FormatierterText';
import { Karte } from '../../components/Karte';
import { Kennzahlen } from '../../components/Kennzahlen';
import { Knopf } from '../../components/Knopf';
import { StatusZeichen } from '../../components/StatusZeichen';
import { formatiereZeitpunkt } from '../../formatierung';
import { abstimmungsTonart } from './AbstimmungenSeite';

/** Welche Aktion gerade zu bestätigen ist (§20.3, §25.3). */
type Bestaetigung = 'oeffnen' | 'schliessen' | 'annullieren' | 'loeschen' | 'warteraum';

/**
 * Einzelansicht einer Abstimmung (§10.3, §14, §15, §17, §18).
 *
 * Hier laufen die kritischen Aktionen der Stufe zusammen. Jede von ihnen verlangt eine
 * Bestätigung, und der Server prüft den Zustand zusätzlich erneut (§20.3, §25.3) — der Dialog
 * allein wäre kein Schutz gegen einen veralteten Tab.
 *
 * **Während eine Abstimmung geöffnet ist, zeigt diese Seite ausschließlich Zahlen** (§15). Eine
 * Verteilung erscheint erst nach dem Schließen; der Server liefert sie vorher gar nicht (§16).
 */
export function AbstimmungSeite(): ReactElement {
  const { id = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const abstimmung = useAbstimmung(id);
  const veranstaltung = useVeranstaltung(abstimmung.data?.veranstaltungId ?? '');
  const geoeffnet = abstimmung.data?.status === 'geoeffnet';
  const geschlossen = abstimmung.data?.status === 'geschlossen';

  const beteiligung = useBeteiligung(id, geoeffnet);
  const ergebnis = useErgebnis(id, geschlossen);
  const aktion = useAbstimmungAktion(id);
  const loeschen = useAbstimmungLoeschen();
  const duplizieren = useAbstimmungDuplizieren();
  const warteraum = useVeranstaltungAktion(abstimmung.data?.veranstaltungId ?? '');

  const [bestaetigung, setBestaetigung] = useState<Bestaetigung | undefined>(undefined);
  const [grund, setGrund] = useState('');

  if (abstimmung.isPending) {
    return <p className="text-text-gedaempft text-sm">Wird geladen …</p>;
  }

  if (abstimmung.isError) {
    return <Fehlermeldung>{abstimmung.error.message}</Fehlermeldung>;
  }

  const daten: Abstimmung = abstimmung.data;
  const zahlen: Beteiligung | undefined = beteiligung.data;
  const keineTeilnehmer = (zahlen?.beruecksichtigteTeilnehmer ?? 0) === 0;

  // §14.4: schlägt eine Plausibilitätsprüfung fehl, muss das Backend es deutlich anzeigen.
  const plausibilitaetFehler =
    aktion.error?.code === API_ERROR_CODES.PLAUSIBILITAET_FEHLGESCHLAGEN ? aktion.error : undefined;

  function fuehreAus(): void {
    if (bestaetigung === undefined) {
      return;
    }

    if (bestaetigung === 'loeschen') {
      loeschen.mutate(id, {
        onSuccess: () => {
          void navigate(`/verwaltung/veranstaltungen/${daten.veranstaltungId}/abstimmungen`);
        },
      });
      return;
    }

    // §19.2: eine Aktion an der Veranstaltung, nicht an dieser Abstimmung — sie räumt nur die
    // Teilnehmeransicht ab und lässt Ergebnis, Export und Protokoll unberührt.
    if (bestaetigung === 'warteraum') {
      warteraum.mutate('warteraum', {
        onSuccess: () => {
          setBestaetigung(undefined);
        },
      });
      return;
    }

    aktion.mutate(
      {
        aktion: bestaetigung,
        ...(bestaetigung === 'annullieren' && grund.trim() !== '' ? { grund: grund.trim() } : {}),
        // §25.3: der Status, den diese Seite angezeigt hat. Hat sich der Serverstand seither
        // geändert, wird die Aktion abgewiesen statt ausgeführt.
        erwarteterStatus: daten.status,
      },
      {
        onSuccess: () => {
          setBestaetigung(undefined);
          setGrund('');
        },
      },
    );
  }

  return (
    <div className="flex max-w-3xl flex-col gap-4">
      <div>
        <p className="text-text-gedaempft text-sm">
          <Link
            to={`/verwaltung/veranstaltungen/${daten.veranstaltungId}/abstimmungen`}
            className="hover:text-akzent underline"
          >
            {veranstaltung.data?.bezeichnung ?? 'Abstimmungen'}
          </Link>
        </p>
        <div className="mt-1 flex flex-wrap items-center gap-3">
          <h1 className="text-xl font-semibold">
            {daten.nummer !== null && (
              <span className="text-text-gedaempft">Nr. {daten.nummer} · </span>
            )}
            {daten.ueberschrift}
          </h1>
          <StatusZeichen
            beschriftung={ABSTIMMUNGSSTATUS_BESCHRIFTUNG[daten.status]}
            tonart={abstimmungsTonart(daten.status)}
          />
        </div>
      </div>

      {plausibilitaetFehler !== undefined && (
        <Fehlermeldung ausfuehrlich>{plausibilitaetFehler.message}</Fehlermeldung>
      )}

      {aktion.isError && plausibilitaetFehler === undefined && (
        <Fehlermeldung>{aktion.error.message}</Fehlermeldung>
      )}
      {loeschen.isError && <Fehlermeldung>{loeschen.error.message}</Fehlermeldung>}

      {/* Vorschau — §10.3 verlangt sie vor dem Öffnen ausdrücklich. */}
      <Karte>
        <h2 className="text-base font-semibold">Vorschau</h2>
        <div className="mt-3">
          <FormatierterText text={daten.text} />
        </div>

        <p className="text-text-gedaempft mt-4 text-sm">
          {beschreibeAuswahlregel(daten.auswahlart, daten.stimmenanzahl, daten.auswahlregel)}
        </p>

        <ol className="mt-2 flex flex-col gap-1">
          {daten.optionen.map((option) => (
            <li
              key={option.id}
              className={[
                'border-rand rounded-md border px-3 py-2 text-sm',
                option.art === 'regulaer' ? '' : 'text-text-gedaempft italic',
              ].join(' ')}
            >
              {option.text}
            </li>
          ))}
        </ol>
      </Karte>

      {geoeffnet && (
        <Karte betont>
          <h2 className="text-base font-semibold">Beteiligung</h2>
          <p className="text-text-gedaempft mt-1 text-sm">
            Während einer geöffneten Abstimmung sind ausschließlich diese Zahlen sichtbar. Die
            Stimmenverteilung wird erst nach dem Schließen berechnet.
          </p>

          <div className="mt-4">
            <Kennzahlen
              gross
              zahlen={[
                { beschriftung: 'Berücksichtigt', wert: zahlen?.beruecksichtigteTeilnehmer },
                { beschriftung: 'Abgegeben', wert: zahlen?.abgegebeneStimmen },
                { beschriftung: 'Ausstehend', wert: zahlen?.nichtAbgegebeneStimmen },
              ]}
            />
          </div>
        </Karte>
      )}

      {geschlossen && ergebnis.data !== undefined && (
        <Karte>
          <h2 className="text-base font-semibold">Ergebnis</h2>

          <div className="mt-3">
            <Kennzahlen
              zahlen={[
                {
                  beschriftung: 'Berücksichtigte Teilnehmer',
                  wert: ergebnis.data.beruecksichtigteTeilnehmer,
                },
                { beschriftung: 'Abgegebene Stimmen', wert: ergebnis.data.abgegebeneStimmen },
                { beschriftung: 'Nicht abgegeben', wert: ergebnis.data.nichtAbgegebeneStimmen },
              ]}
            />
          </div>

          <div className="mt-5">
            <Ergebnisbalken
              optionen={ergebnis.data.optionen}
              abgegebeneStimmen={ergebnis.data.abgegebeneStimmen}
            />
          </div>

          <p className="text-text-gedaempft mt-4 text-xs">
            Die Einzelstimmzettel wurden nach der Plausibilitätsprüfung gelöscht. Verbindlich sind
            ausschließlich diese geprüften Werte.
          </p>
        </Karte>
      )}

      {geschlossen && <Feststellungskarte abstimmung={daten} ergebnis={ergebnis.data} />}

      {geschlossen && <Exportkarte abstimmungId={id} />}

      <Karte>
        <h2 className="text-base font-semibold">Aktionen</h2>

        <div className="mt-3 flex flex-wrap gap-2">
          {daten.status === 'entwurf' && (
            <>
              <Knopf
                disabled={aktion.isPending}
                onClick={() => {
                  aktion.mutate({ aktion: 'bereit' });
                }}
              >
                Als bereit markieren
              </Knopf>
              <Link
                to="bearbeiten"
                className="border-rand bg-flaeche-gedaempft min-h-touchflaeche hover:bg-rand flex items-center rounded-md border px-4 font-medium"
              >
                Bearbeiten
              </Link>
            </>
          )}

          {daten.status === 'bereit' && (
            <>
              <Knopf
                disabled={aktion.isPending}
                onClick={() => {
                  setBestaetigung('oeffnen');
                }}
              >
                Abstimmung öffnen
              </Knopf>
              <Knopf
                art="sekundaer"
                disabled={aktion.isPending}
                onClick={() => {
                  aktion.mutate({ aktion: 'entwurf' });
                }}
              >
                Zurück in den Entwurf
              </Knopf>
            </>
          )}

          {/*
            Die Reihenfolge wird in der Übersicht geändert, nicht hier: dort stehen die
            Abstimmungen untereinander, und nur dort ist zu sehen, wohin ein Schritt führt.
          */}
          {(daten.status === 'entwurf' || daten.status === 'bereit') && (
            <Knopf
              art="gefahr"
              disabled={loeschen.isPending}
              onClick={() => {
                setBestaetigung('loeschen');
              }}
            >
              Löschen
            </Knopf>
          )}

          {/* §10.3: Duplizieren geht in jedem Status — auch als Grundlage einer Wiederholung. */}
          <Knopf
            art="sekundaer"
            disabled={duplizieren.isPending}
            onClick={() => {
              duplizieren.mutate(id, {
                onSuccess: (kopie) => {
                  void navigate(`/verwaltung/abstimmungen/${kopie.id}`);
                },
              });
            }}
          >
            Duplizieren
          </Knopf>

          {geoeffnet && (
            <>
              <Knopf
                disabled={aktion.isPending}
                onClick={() => {
                  setBestaetigung('schliessen');
                }}
              >
                Abstimmung schließen
              </Knopf>
              <Knopf
                art="gefahr"
                disabled={aktion.isPending}
                onClick={() => {
                  setBestaetigung('annullieren');
                }}
              >
                Annullieren
              </Knopf>
            </>
          )}

          {geschlossen &&
            (daten.ergebnisVeroeffentlichtAm === null ? (
              <Knopf
                disabled={aktion.isPending}
                onClick={() => {
                  aktion.mutate({ aktion: 'ergebnis/veroeffentlichen' });
                }}
              >
                Ergebnis veröffentlichen
              </Knopf>
            ) : (
              <Knopf
                art="sekundaer"
                disabled={aktion.isPending}
                onClick={() => {
                  aktion.mutate({ aktion: 'ergebnis/beenden' });
                }}
              >
                Ergebnisanzeige beenden
              </Knopf>
            ))}

          {/*
            §19.2: Nach dem Schließen bleibt der Abschlusshinweis beziehungsweise das Ergebnis auf
            den Teilnehmergeräten stehen. Diese Aktion holt alle zurück in den Warteraum.
          */}
          {(geschlossen || daten.status === 'annulliert') &&
            veranstaltung.data !== undefined &&
            istAktiv(veranstaltung.data.status) && (
              <Knopf
                art="sekundaer"
                disabled={warteraum.isPending}
                onClick={() => {
                  setBestaetigung('warteraum');
                }}
              >
                Alle Teilnehmer in den Warteraum
              </Knopf>
            )}
        </div>

        {warteraum.isError && <Fehlermeldung>{warteraum.error.message}</Fehlermeldung>}

        {geschlossen && (
          <p className="text-text-gedaempft mt-3 text-sm">
            {daten.ergebnisVeroeffentlichtAm === null
              ? 'Die Teilnehmer sehen derzeit nur den neutralen Hinweis, dass die Abstimmung beendet ist.'
              : `Veröffentlicht seit ${formatiereZeitpunkt(daten.ergebnisVeroeffentlichtAm)}. Das Ergebnis ist auf allen verbundenen Teilnehmergeräten sichtbar.`}
          </p>
        )}

        {daten.status === 'annulliert' && (
          <p className="text-text-gedaempft mt-3 text-sm">
            Diese Abstimmung wurde annulliert. Ein Ergebnis wird nicht veröffentlicht; eine
            Wiederholung ist nur als neue Abstimmung möglich.
            {daten.annullierungsgrund !== null && (
              <> Interner Grund: „{daten.annullierungsgrund}"</>
            )}
          </p>
        )}
      </Karte>

      {bestaetigung !== undefined && (
        <BestaetigungsDialog
          titel={TITEL[bestaetigung]}
          beschreibung={
            <Beschreibung
              art={bestaetigung}
              zahlen={zahlen}
              keineTeilnehmer={keineTeilnehmer}
              grund={grund}
              onGrund={setGrund}
            />
          }
          bestaetigungstext={BESTAETIGUNG[bestaetigung]}
          gefahr={bestaetigung !== 'oeffnen' && bestaetigung !== 'warteraum'}
          laeuft={aktion.isPending || loeschen.isPending || warteraum.isPending}
          onBestaetigen={fuehreAus}
          onAbbrechen={() => {
            setBestaetigung(undefined);
            setGrund('');
          }}
        />
      )}
    </div>
  );
}

const TITEL: Record<Bestaetigung, string> = {
  oeffnen: 'Abstimmung öffnen?',
  schliessen: 'Abstimmung endgültig schließen?',
  annullieren: 'Abstimmung annullieren?',
  loeschen: 'Abstimmungsentwurf löschen?',
  warteraum: 'Alle Teilnehmer in den Warteraum setzen?',
};

const BESTAETIGUNG: Record<Bestaetigung, string> = {
  oeffnen: 'Jetzt öffnen',
  schliessen: 'Endgültig schließen',
  annullieren: 'Annullieren',
  loeschen: 'Löschen',
  warteraum: 'In den Warteraum setzen',
};

/** Die Texte der Bestätigungsdialoge — §17.2 gibt für das Schließen fünf Angaben vor. */
function Beschreibung({
  art,
  zahlen,
  keineTeilnehmer,
  grund,
  onGrund,
}: {
  art: Bestaetigung;
  zahlen: Beteiligung | undefined;
  keineTeilnehmer: boolean;
  grund: string;
  onGrund: (wert: string) => void;
}): ReactNode {
  if (art === 'oeffnen') {
    return (
      <>
        <p>
          Die Abstimmung wird für alle angemeldeten Teilnehmer sichtbar. Inhalt und Regeln sind
          danach unveränderlich, und die Abstimmungsnummer steht dauerhaft fest.
        </p>
        {keineTeilnehmer && (
          <p className="text-warnung mt-2 font-medium">
            Achtung: Es ist derzeit kein Teilnehmer angemeldet. Die Abstimmung kann trotzdem
            geöffnet werden — sie hätte dann keine Stimmberechtigten.
          </p>
        )}
      </>
    );
  }

  if (art === 'schliessen') {
    return (
      <>
        <ul className="list-disc pl-5">
          <li>Berücksichtigte Teilnehmer: {zahlen?.beruecksichtigteTeilnehmer ?? '—'}</li>
          <li>Abgegebene Stimmen: {zahlen?.abgegebeneStimmen ?? '—'}</li>
          <li>Noch nicht abgegeben: {zahlen?.nichtAbgegebeneStimmen ?? '—'}</li>
        </ul>
        <p className="mt-2">
          Das Schließen ist endgültig: die Abstimmung kann nicht erneut geöffnet werden, und eine
          weitere Stimmabgabe ist ausgeschlossen. Die Einzelstimmzettel werden nach der
          Plausibilitätsprüfung gelöscht.
        </p>
      </>
    );
  }

  if (art === 'warteraum') {
    return (
      <p>
        Auf allen Teilnehmergeräten erscheint wieder der Warteraum mit dem Hinweis, dass derzeit
        keine Abstimmung ansteht; die zuletzt beendete Abstimmung wird ihnen nicht mehr angezeigt,
        und eine laufende Ergebnisanzeige endet. Ergebnis, Export und Protokoll bleiben unverändert.
        Sobald die nächste Abstimmung geöffnet wird, erscheint sie von selbst.
      </p>
    );
  }

  if (art === 'annullieren') {
    return (
      <>
        <p>
          Die Abstimmung wird endgültig verworfen. Alle bereits abgegebenen Stimmzettel und
          Teilnahmedaten werden gelöscht, ein Ergebnis wird nicht veröffentlicht. Eine Wiederholung
          ist nur als neue Abstimmung möglich.
        </p>
        <label className="mt-3 flex flex-col gap-1">
          <span className="text-sm font-medium">Interner Grund (optional)</span>
          <textarea
            value={grund}
            rows={2}
            maxLength={1000}
            onChange={(ereignis) => {
              onGrund(ereignis.target.value);
            }}
            className="border-rand bg-hintergrund rounded-md border px-3 py-2"
          />
          <span className="text-text-gedaempft text-xs">
            Ausschließlich im Administrationsbereich sichtbar.
          </span>
        </label>
      </>
    );
  }

  return (
    <p>
      Der Entwurf wird endgültig gelöscht. Da er nie geöffnet wurde, entsteht dadurch keine
      Nummernlücke.
    </p>
  );
}

/**
 * Exporte dieser Abstimmung (§27.1, §27.3, §27.4).
 *
 * Dieselbe Darstellung wie auf der Seite „Ergebnisse und Exporte" — sie steht hier zusätzlich,
 * weil der Administrator nach dem Schließen ohnehin auf dieser Seite ist und §27.3 ausdrücklich
 * eine Warnung bei fehlendem Export verlangt. Die Warnung bleibt ein Hinweis: sie blockiert weder
 * die nächste Abstimmung noch das Veranstaltungsende.
 */
function Exportkarte({ abstimmungId }: { abstimmungId: string }): ReactElement {
  const stand = useAbstimmungExportstand(abstimmungId, true);
  const exportieren = useAbstimmungExportieren();
  const loeschen = useExportLoeschen();
  const [zuLoeschen, setZuLoeschen] = useState<ExportEintrag | null>(null);

  const laeuft = exportieren.isPending || loeschen.isPending;
  const fehler = exportieren.error ?? loeschen.error ?? null;

  return (
    <Karte>
      <h2 className="text-base font-semibold">Exporte</h2>
      <p className="text-text-gedaempft mt-1 mb-3 text-sm">
        {stand.data?.exportiert === true ? (
          'Diese Abstimmung wurde exportiert.'
        ) : (
          <span className="text-warnung">
            Diese Abstimmung wurde noch nicht exportiert. Das hindert weder die nächste Abstimmung
            noch das Veranstaltungsende.
          </span>
        )}
      </p>

      {fehler !== null && (
        <div className="mb-3">
          <Fehlermeldung>{fehler.message}</Fehlermeldung>
        </div>
      )}

      {zuLoeschen !== null && (
        <div className="mb-3">
          <BestaetigungsDialog
            titel="Exportdatei löschen?"
            beschreibung={
              <>
                Die Datei <code>{zuLoeschen.dateiname}</code> wird gelöscht. Sie kann jederzeit neu
                erzeugt werden; die frühere Erstellung bleibt vermerkt.
              </>
            }
            bestaetigungstext="Endgültig löschen"
            gefahr
            laeuft={loeschen.isPending}
            onBestaetigen={() => {
              loeschen.mutate(zuLoeschen.id, { onSuccess: () => setZuLoeschen(null) });
            }}
            onAbbrechen={() => {
              setZuLoeschen(null);
            }}
          />
        </div>
      )}

      <div className="flex flex-col">
        {(['pdf', 'csv', 'json'] as ExportformatWert[]).map((format) => (
          <ExportFormatZeile
            key={format}
            beschriftung={EXPORTFORMAT_BESCHRIFTUNG[format]}
            eintrag={stand.data?.formate.find((zeile) => zeile.format === format)?.eintrag ?? null}
            laeuft={laeuft}
            onErzeugen={() => {
              exportieren.mutate({ abstimmungId, format });
            }}
            onLoeschen={setZuLoeschen}
          />
        ))}
      </div>
    </Karte>
  );
}

/**
 * Ergebnisfeststellung und Berichtigungsvermerke (§18.4, §18.5).
 *
 * Bis zum Ende der Veranstaltung lässt sich die Feststellung ändern und entfernen. Danach ist sie
 * unveränderlich — der Server weist jede Änderung ab —, und Korrekturen laufen ausschließlich über
 * zusätzliche, unveränderliche Vermerke.
 */
function Feststellungskarte({
  abstimmung,
  ergebnis,
}: {
  abstimmung: Abstimmung;
  ergebnis: Ergebnis | undefined;
}): ReactElement {
  const feststellung = useFeststellung(abstimmung.id);
  const berichtigung = useBerichtigung(abstimmung.id);

  const [entwurf, setEntwurf] = useState<string | undefined>(undefined);
  const [vermerk, setVermerk] = useState('');

  const bestehend = ergebnis?.feststellung ?? null;
  const vermerke = ergebnis?.berichtigungen ?? [];

  // Die Unveränderlichkeit nach §18.5 hängt am Ende der Veranstaltung. Sie ergibt sich hier aus
  // der Antwort des Servers; die Oberfläche rät nicht, sondern zeigt seine Meldung.
  const unveraenderlich = feststellung.error?.code === API_ERROR_CODES.STATUSWECHSEL_UNZULAESSIG;

  return (
    <Karte>
      <h2 className="text-base font-semibold">Ergebnisfeststellung</h2>
      <p className="text-text-gedaempft mt-1 text-sm">
        Optional, höchstens {ABSTIMMUNG_GRENZEN.ergebnisfeststellung} Zeichen. Sie verändert keine
        Zahlen — die Anwendung bewertet weder Mehrheiten noch Satzungsregeln.
      </p>

      {entwurf === undefined ? (
        <div className="mt-3 flex flex-wrap items-center gap-3">
          {bestehend === null ? (
            <span className="text-text-gedaempft text-sm">Keine Feststellung eingetragen.</span>
          ) : (
            <p className="border-rand bg-hintergrund flex-1 rounded-md border p-3">{bestehend}</p>
          )}

          <Knopf
            art="sekundaer"
            onClick={() => {
              setEntwurf(bestehend ?? '');
            }}
          >
            {bestehend === null ? 'Feststellung eintragen' : 'Ändern'}
          </Knopf>

          {bestehend !== null && (
            <Knopf
              art="gefahr"
              disabled={feststellung.isPending}
              onClick={() => {
                feststellung.mutate(null);
              }}
            >
              Entfernen
            </Knopf>
          )}
        </div>
      ) : (
        <div className="mt-3 flex flex-col gap-2">
          <textarea
            value={entwurf}
            rows={3}
            maxLength={ABSTIMMUNG_GRENZEN.ergebnisfeststellung}
            aria-label="Ergebnisfeststellung"
            onChange={(ereignis) => {
              setEntwurf(ereignis.target.value);
            }}
            className="border-rand bg-hintergrund rounded-md border px-3 py-2"
          />
          <div className="flex flex-wrap gap-2">
            <Knopf
              disabled={feststellung.isPending || entwurf.trim() === ''}
              onClick={() => {
                feststellung.mutate(entwurf.trim(), { onSuccess: () => setEntwurf(undefined) });
              }}
            >
              Speichern
            </Knopf>
            <Knopf
              art="sekundaer"
              onClick={() => {
                setEntwurf(undefined);
              }}
            >
              Abbrechen
            </Knopf>
          </div>
        </div>
      )}

      {feststellung.isError && <Fehlermeldung>{feststellung.error.message}</Fehlermeldung>}

      {(unveraenderlich || vermerke.length > 0) && (
        <div className="border-rand mt-5 border-t pt-4">
          <h3 className="text-base font-semibold">Berichtigungsvermerke</h3>
          <p className="text-text-gedaempft mt-1 text-sm">
            Nach dem Ende der Veranstaltung ist die Feststellung unveränderlich. Korrekturen
            erfolgen als zusätzliche Vermerke; sie lassen sich weder ändern noch löschen.
          </p>

          {vermerke.length > 0 && (
            <ul className="mt-3 flex flex-col gap-2">
              {vermerke.map((eintrag) => (
                <li key={eintrag.erstelltAm} className="border-rand rounded-md border p-3 text-sm">
                  <span className="text-text-gedaempft block text-xs">
                    {formatiereZeitpunkt(eintrag.erstelltAm)}
                  </span>
                  {eintrag.text}
                </li>
              ))}
            </ul>
          )}

          <div className="mt-3 flex flex-col gap-2">
            <textarea
              value={vermerk}
              rows={2}
              maxLength={ABSTIMMUNG_GRENZEN.ergebnisfeststellung}
              aria-label="Neuer Berichtigungsvermerk"
              onChange={(ereignis) => {
                setVermerk(ereignis.target.value);
              }}
              className="border-rand bg-hintergrund rounded-md border px-3 py-2"
            />
            <div>
              <Knopf
                disabled={berichtigung.isPending || vermerk.trim() === ''}
                onClick={() => {
                  berichtigung.mutate(vermerk.trim(), { onSuccess: () => setVermerk('') });
                }}
              >
                Vermerk anfügen
              </Knopf>
            </div>
            {berichtigung.isError && <Fehlermeldung>{berichtigung.error.message}</Fehlermeldung>}
          </div>
        </div>
      )}
    </Karte>
  );
}
